import { type PropsWithChildren, useCallback } from "react";
import { Flex, Heading, Text, Button, useMultiStyleConfig, Image, useTheme, useMediaQuery } from "@chakra-ui/react";
import type { FlexProps, TextProps, ImageProps } from "@chakra-ui/react";
import { AiOutlinePlus, AiOutlineMinus } from "react-icons/ai";
import { ContentGroup } from "@/components/ContentGroup/ContentGroup";
import { FeatureEnabled } from "@/services/Features";
import { useInstanceConfig } from "@/services/InstanceConfigContext";
import { ItemActionSourceEnum } from "@/services/analyticsEmitter";
import { collectionManagerEmitter } from "@/services/collectionManagerEmitter";
import { compareManagerEmitter } from "@/services/compareManagerEmitter";
import { useCompareManagerState } from "@/services/useCompareManager";
import { useHtmlifiedTranslations } from "@/services/useHtmlifiedTranslations";
import { htmlify, interpolatePlaceholders } from "@/services/utils";
import type { ShareClassTranslationMap } from "@/types/ShareClassTranslationMap";

export interface FundDetailPageHeaderProps extends FlexProps {
	heading: string;
	headingColor: string;
	brandIcon: string;
	brandIconWidth: number;
	backgroundImage: string;
	date: string;
	identifier: string;
	identifierLabel: string;
	dateLabel: string;
	baseDateLabel: string;
	baseDate: string;
	externalId: string;
	translations: ShareClassTranslationMap;
	compareEnabled?: boolean;
	bannerEnabled?: boolean;
}

export const FundDetailPageHeader = ({
	children,
	backgroundImage,
	heading,
	headingColor,
	brandIcon,
	brandIconWidth,
	date,
	dateLabel,
	identifier,
	identifierLabel,
	baseDate,
	baseDateLabel,
	externalId,
	translations,
	compareEnabled = true,
	...rest
}: PropsWithChildren<FundDetailPageHeaderProps>) => {
	const instanceConfig = useInstanceConfig();
	const setInternalShelfPage = useCallback(() => {}, []);
	const { externalIds } = useCompareManagerState({ setInternalShelfPage });
	const theme = useTheme();
	const [isDesktop] = useMediaQuery(`(min-width: ${theme?.breakpoints?.lg || 0})`);
	const styleParts = useMultiStyleConfig("FundDetailPageHeader");
	const htmlifiedTranslations = useHtmlifiedTranslations(translations) as ShareClassTranslationMap;
	const useH1 = instanceConfig.misc?.useH1 ?? false;

	const handleToggleCollection = () => {
		collectionManagerEmitter.emit("AddItemToCollectionOpen", externalId, ItemActionSourceEnum.FundDetailPage);
	};

	const handleToggleCompare = () => {
		compareManagerEmitter.emit("ItemSelectionToggle", externalId, ItemActionSourceEnum.FundDetailPage);
	};

	const compareButtonDisabled = externalIds.length >= 3 && !(externalIds.indexOf(externalId) > -1);

	return (
		<Flex {...(styleParts.root as FlexProps)} className="FundDetailPageHeader" position="relative">
			{backgroundImage && (
				<Image
					src={backgroundImage}
					objectFit="cover"
					position="absolute"
					top={0}
					left={0}
					width="100%"
					height="100%"
					alt="Brand header"
					aria-hidden="true"
				/>
			)}

			<ContentGroup
				borderTop="none"
				background="linear-gradient(to left, rgba(255, 255, 255, 0) 0, #000 100%)"
				flexDirection="column"
				bgColor="transparent"
				justifyContent="center"
				zIndex={2}
			>
				<Flex {...(styleParts.contentFlex as FlexProps)}>
					{brandIcon && (
						<Image
							{...(styleParts.logo as ImageProps)}
							loading="lazy"
							maxWidth={brandIconWidth}
							src={brandIcon}
							alt="Brand Icon"
							aria-hidden="true"
						/>
					)}
					<Flex flexDirection="column" position="relative" height="inherit">
						<Flex flexGrow={1} alignItems="center">
							<Flex sx={styleParts.textContent1} height="fit-content">
								<Heading
									as={useH1 ? "h1" : "h2"}
									sx={{
										...styleParts.heading,
										...(headingColor
											? {
													color: headingColor
												}
											: {})
									}}
								>
									{htmlify(heading)}
								</Heading>
								<Flex {...(styleParts.textContent2 as FlexProps)}>
									{identifier && (
										<Text variant="content" {...(styleParts.contentText as TextProps)}>
											{htmlify(identifierLabel)}:{" "}
											<Text as="span" variant="digit" fontSize="lg" {...(styleParts.isinText as TextProps)}>
												{identifier}
											</Text>
										</Text>
									)}
									<Text variant="content" {...(styleParts.contentText as TextProps)}>
										{htmlify(dateLabel)}:{" "}
										<Text as="span" variant="digit" {...(styleParts.contentText as TextProps)}>
											{date}
										</Text>
									</Text>
								</Flex>
								{baseDate && (
									<Flex {...(styleParts.textContent2 as FlexProps)}>
										<Text variant="content">
											{htmlify(baseDateLabel)}:{" "}
											<Text as="span" variant="digit" fontSize="lg" {...(styleParts.isinText as TextProps)}>
												{interpolatePlaceholders({ text: baseDate, instanceConfig })}
											</Text>
										</Text>
									</Flex>
								)}
							</Flex>
						</Flex>

						{/* desktop */}
						<Flex
							display={{ base: "none", md: "inherit" }}
							flexDirection={{ base: "column", md: "row" }}
							// position="absolute"
							// bottom={0}
							// left={0}
						>
							<FeatureEnabled features="collections">
								<Button
									aria-haspopup="dialog"
									sx={styleParts.compareCollectionButton}
									leftIcon={<AiOutlinePlus size={20} />}
									onClick={handleToggleCollection}
									paddingLeft={0}
								>
									{htmlifiedTranslations.addToWatchlist ? htmlifiedTranslations.addToWatchlist : "Add to watchlist"}
								</Button>
							</FeatureEnabled>
							{compareEnabled && isDesktop && (
								<FeatureEnabled features="compare">
									<Button
										isDisabled={compareButtonDisabled}
										aria-disabled={compareButtonDisabled ? "true" : "false"}
										sx={
											compareButtonDisabled
												? styleParts.compareCollectionButtonDisabled
												: styleParts.compareCollectionButton
										}
										leftIcon={
											externalIds.indexOf(externalId) > -1 ? (
												<AiOutlineMinus size={20} />
											) : (
												<AiOutlinePlus size={20} />
											)
										}
										onClick={compareButtonDisabled ? undefined : handleToggleCompare}
									>
										{externalIds.indexOf(externalId) > -1
											? `${htmlifiedTranslations.removeFromComparison}(${externalIds.length}/3)`
											: `${htmlifiedTranslations.addToComparison}(${externalIds.length}/3)`}
									</Button>
								</FeatureEnabled>
							)}
						</Flex>

						{/* mobile */}
						<Flex sx={styleParts.buttonWrapper}>
							<FeatureEnabled features="collections">
								<Button
									sx={styleParts.compareCollectionButton}
									leftIcon={<AiOutlinePlus size={20} />}
									onClick={handleToggleCollection}
									paddingLeft={0}
								>
									{htmlifiedTranslations.addToWatchlist ? htmlifiedTranslations.addToWatchlist : "Add to watchlist"}
								</Button>
							</FeatureEnabled>
						</Flex>
					</Flex>
				</Flex>
				{children && <Flex {...rest}>{children}</Flex>}
			</ContentGroup>
		</Flex>
	);
};
